import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Alert, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import { German } from 'flatpickr/dist/l10n/de';
import 'flatpickr/dist/themes/material_blue.css';
import config from '../../config';
import { getLoggedinUser } from '../../helpers/api_helper';
import './QuickTaskSpotlight.css';

interface QuickTaskSpotlightProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated?: () => void;
}


interface User {
  id: number;
  name: string;
  email: string;
}

interface Project {
  id: number;
  title: string;
  num: number;
}

const priorityOptions = [
  { value: 'low', label: 'Niedrig', color: '#28a745' },
  { value: 'medium', label: 'Mittel', color: '#ffc107' },
  { value: 'high', label: 'Hoch', color: '#dc3545' }
];

const QuickTaskSpotlight: React.FC<QuickTaskSpotlightProps> = ({ isOpen, onClose, onTaskCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState<User | User[] | null>(null);
  const [isMultiAssign, setIsMultiAssign] = useState(false);
  const [priority, setPriority] = useState<string>('medium');
  const [dueDate, setDueDate] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>('');
  
  const titleInputRef = useRef<HTMLInputElement>(null);
  const currentUser = getLoggedinUser();

  const fetchUsers = useCallback(async () => {
    if (!currentUser || !currentUser.token) {
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await fetch(`${config.api.API_URL}/users?pageSize=1000&sortBy=full_name&sortOrder=asc&active=true`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [currentUser]);

  const fetchProjects = useCallback(async () => {
    if (!currentUser || !currentUser.token) {
      return;
    }

    setLoadingProjects(true);
    try {
      const response = await fetch(`${config.api.API_URL}/projects?pageSize=100&includeStats=false`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  }, [currentUser]);

  const handleClose = useCallback(() => {
    setTitle('');
    setDescription('');
    setSelectedAssignee(null);
    setIsMultiAssign(false);
    setPriority('medium');
    setDueDate('');
    setSelectedProject(null);
    setError('');
    onClose();
  }, [onClose]);

  // Load users and projects when opened
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchProjects();
      // Focus title input
      setTimeout(() => {
        if (titleInputRef.current) {
          titleInputRef.current.focus();
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Remove fetchUsers/fetchProjects from dependencies to avoid infinite loop

  // Global ESC key listener
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isOpen, handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser.token) {
      setError('Nicht authentifiziert. Bitte erneut anmelden.');
      return;
    }
    
    if (!title.trim()) {
      setError('Titel ist erforderlich');
      return;
    }

    if (!selectedAssignee || (Array.isArray(selectedAssignee) && selectedAssignee.length === 0)) {
      setError('Bitte wähle mindestens eine Person aus');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const payload: any = {
        title: title.trim(),
        description: description.trim() || undefined,
        priority: priority,
        assigned_to: isMultiAssign && Array.isArray(selectedAssignee)
          ? selectedAssignee.map(u => u.id)
          : (selectedAssignee as User)?.id
      };

      if (dueDate) {
        payload.due_date = dueDate;
      }

      if (selectedProject) {
        payload.project_id = selectedProject.id;
      }

      const response = await fetch(`${config.api.API_URL}/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        handleClose();
        if (onTaskCreated) {
          onTaskCreated();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Fehler beim Erstellen der Aufgabe');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Fehler beim Erstellen der Aufgabe');
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    } else if (e.key === 'Enter' && !e.shiftKey && selectedAssignee && title.trim()) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '38px',
      fontSize: '14px',
      borderRadius: '0.375rem',
      borderColor: 'var(--vz-input-border)',
      '&:hover': {
        borderColor: 'var(--vz-input-focus-border)'
      }
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 10000
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 10000
    })
  };


  if (!isOpen) return null;

  return (
    <div className="quick-task-overlay" onClick={handleClose}>
      <div className="quick-task-spotlight" onClick={(e) => e.stopPropagation()}>
        <div className="quick-task-header">
          <h4>Neue Aufgabe</h4>
          <button className="close-button" onClick={handleClose}>
            <i className="ri-close-line"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          <div className="quick-task-body">
            {error && (
              <Alert color="danger" className="mb-3" fade={false}>
                {error}
              </Alert>
            )}

            {/* Title */}
            <div className="mb-3">
              <input
                ref={titleInputRef}
                type="text"
                className="form-control form-control-lg"
                placeholder="Aufgabe beschreiben..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={creating}
                maxLength={255}
              />
            </div>

            {/* Description */}
            <div className="mb-3">
              <textarea
                className="form-control"
                rows={2}
                placeholder="Zusätzliche Details (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={creating}
              />
            </div>

            {/* Priority and Due Date Row */}
            <Row className="mb-3">
              <Col md={6}>
                <label className="form-label small fw-semibold">Priorität</label>
                <Select
                  value={priorityOptions.find(p => p.value === priority)}
                  onChange={(option: any) => setPriority(option?.value || 'medium')}
                  options={priorityOptions}
                  isDisabled={creating}
                  styles={{
                    ...selectStyles,
                    option: (base: any, state: any) => ({
                      ...base,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      '&::before': {
                        content: '""',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: state.data.color
                      }
                    }),
                    singleValue: (base: any, state: any) => ({
                      ...base,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      '&::before': {
                        content: '""',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: state.data.color
                      }
                    })
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Col>
              <Col md={6}>
                <label className="form-label small fw-semibold">Fällig am</label>
                <Flatpickr
                  className="form-control"
                  value={dueDate}
                  options={{
                    dateFormat: "d.m.Y",
                    locale: German,
                    altInput: true,
                    altFormat: "d.m.Y",
                    allowInput: true,
                    minDate: "today"
                  }}
                  onChange={(selectedDates) => {
                    if (selectedDates.length > 0) {
                      const date = selectedDates[0];
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      setDueDate(`${year}-${month}-${day}`);
                    } else {
                      setDueDate('');
                    }
                  }}
                  placeholder="Optional"
                  disabled={creating}
                />
              </Col>
            </Row>

            {/* Project */}
            <div className="mb-3">
              <label className="form-label small fw-semibold">Projekt (optional)</label>
              <Select
                value={selectedProject ? { value: selectedProject.id, label: `${selectedProject.num} - ${selectedProject.title}` } : null}
                onChange={(option: any) => {
                  if (option) {
                    const project = projects.find(p => p.id === option.value);
                    setSelectedProject(project || null);
                  } else {
                    setSelectedProject(null);
                  }
                }}
                options={projects.map(p => ({
                  value: p.id,
                  label: `${p.num} - ${p.title}`
                }))}
                placeholder="Projekt auswählen..."
                isClearable
                isLoading={loadingProjects}
                isDisabled={creating}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            {/* Assignee */}
            <div className="mb-3">
              <label className="form-label small fw-semibold">Zuweisen an *</label>
              <FormGroup check className="mb-2">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={isMultiAssign}
                    onChange={(e) => {
                      setIsMultiAssign(e.target.checked);
                      if (!e.target.checked && Array.isArray(selectedAssignee)) {
                        // Switch to single selection
                        setSelectedAssignee(selectedAssignee[0] || null);
                      } else if (e.target.checked && selectedAssignee && !Array.isArray(selectedAssignee)) {
                        // Switch to multi selection
                        setSelectedAssignee([selectedAssignee]);
                      }
                    }}
                    disabled={creating}
                  />{' '}
                  Mehrere Personen zuweisen
                </Label>
              </FormGroup>
              <Select
                value={isMultiAssign && Array.isArray(selectedAssignee)
                  ? selectedAssignee.map(u => ({ value: u.id, label: u.name }))
                  : selectedAssignee && !Array.isArray(selectedAssignee) 
                    ? { value: selectedAssignee.id, label: selectedAssignee.name } 
                    : null}
                onChange={(selectedOptions: any) => {
                  if (isMultiAssign) {
                    if (selectedOptions && selectedOptions.length > 0) {
                      const selectedUsers = selectedOptions.map((opt: any) => 
                        users.find(u => u.id === opt.value)
                      ).filter(Boolean);
                      setSelectedAssignee(selectedUsers);
                    } else {
                      setSelectedAssignee([]);
                    }
                  } else {
                    if (selectedOptions) {
                      const user = users.find(u => u.id === selectedOptions.value);
                      setSelectedAssignee(user || null);
                    } else {
                      setSelectedAssignee(null);
                    }
                  }
                }}
                options={users.map(u => ({
                  value: u.id,
                  label: u.name
                }))}
                placeholder={isMultiAssign ? "Personen auswählen..." : "Person auswählen..."}
                isClearable={false}
                isMulti={isMultiAssign}
                isLoading={loadingUsers}
                isDisabled={creating}
                styles={selectStyles}
                closeMenuOnSelect={!isMultiAssign}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

          </div>

          <div className="quick-task-footer">
            <div className="shortcuts-hint">
              <span><kbd>Enter</kbd> zum Erstellen</span>
              <span><kbd>Esc</kbd> zum Schließen</span>
            </div>
            {selectedAssignee && (
              <button 
                type="submit"
                className="send-button"
                disabled={!title.trim() || !selectedAssignee || creating}
                style={{
                  backgroundColor: '#0066ff',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                {creating ? (
                  <>
                    <Spinner size="sm" />
                    Erstellen...
                  </>
                ) : (
                  <>
                    <i className="ri-add-line"></i>
                    Erstellen
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickTaskSpotlight;